import { tabbedDataGridPage } from "../../styles/commonPageStyles";

const styles = {
  viewSelectForm: {
    width: "25%",
    mt: "5px",
    ml: "2px",
    paddingX: "5px",
  },
  viewSelectSelect: {
    height: "35px",
  },
  // Portal-style header controls: labelled compact selects + primary Reload.
  topControlsStack: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-end",
    gap: 1,
  },
  filterLabel: (colors) => ({
    fontSize: "10.5px",
    color: colors.grey[300],
    mb: "3px",
  }),
  filterSelect: {
    height: "32px",
    minWidth: "140px",
    fontSize: "13px",
  },
  // Same height as the selects so the flex-end row lines up cleanly.
  reloadButton: {
    m: "0px",
    width: "fit-content",
    height: "32px",
  },
  reloadIcon: { fontSize: 16, color: "#fff" },
  cloudsCellBox: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
  },
  // Table fills the height remaining below the header controls.
  viewTable: { flex: 1, minHeight: 0 },
  cloudIconTextBox: {
    display: "inline",
    fontSize: "large",
  },
  scriptBox: {
    display: "flex",
    flexDirection: "row",
    mb: "0px",
    pb: "0px",
    mt: "auto",
  },
};

export const submitRequestDialogStyles = {
  gridContainer: {
    padding: "7px",
    width: "50vw",
  },
  formControl: { width: "100%" },
  // Right-side drawer shell shared by both patch request forms: fixed
  // header, scrollable body, fixed footer.
  drawerPaper: (width = 700) => ({
    width: { xs: "100%", sm: width },
    maxWidth: "100vw",
    display: "flex",
    flexDirection: "column",
  }),
  drawerHeader: {
    p: "14px 20px",
    borderBottom: "1px solid",
    borderColor: "divider",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 2,
    flexShrink: 0,
  },
  // Matches RefreshDrawer's bordered, hover-highlighted close button.
  closeButton: {
    flexShrink: 0,
    width: "30px",
    height: "30px",
    borderRadius: "6px",
    border: "1px solid",
    borderColor: "divider",
    color: "text.secondary",
    "&:hover": {
      backgroundColor: "action.hover",
      borderColor: "primary.main",
      color: "primary.main",
    },
  },
  title: { fontSize: "15px", fontWeight: 700 },
  subtitle: { fontSize: "12.5px", color: "text.secondary", mt: "2px" },
  drawerBody: { flex: 1, minHeight: 0, overflowY: "auto", p: "18px 20px" },
  drawerFooter: {
    borderTop: "1px solid",
    borderColor: "divider",
    bgcolor: "grey.50",
    px: 2.5,
    py: 1.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexShrink: 0,
  },
  // Read-only context block at the top of the body; values come from state,
  // not from inputs.
  contextBlock: {
    bgcolor: "grey.50",
    borderRadius: "8px",
    p: "12px 14px",
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
    gap: "10px 16px",
    mb: 2.5,
  },
  contextLabel: {
    fontFamily: "monospace",
    fontSize: "10px",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "text.secondary",
  },
  contextValue: {
    fontFamily: "monospace",
    fontSize: "12px",
    color: "text.primary",
  },
  // Label on the left, "was {old}" hint right-aligned on the same line.
  labelRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 1,
  },
  wasHint: {
    fontFamily: "monospace",
    fontSize: "11px",
    color: "text.secondary",
  },
  fieldRow: { mb: 2 },
  twoColRow: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
    gap: 1.5,
  },
  // BUILD FOR: selectable cloud cards stacked vertically.
  cloudCardsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 1.5,
    alignItems: "start",
  },
  cloudCard: (colors, selected) => ({
    border: "1px solid",
    borderColor: selected ? "primary.main" : "grey.300",
    bgcolor: selected ? colors.tint : "transparent",
    borderRadius: "8px",
    p: 1.25,
  }),
  cloudCardHead: {
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    cursor: "pointer",
  },
  // The head row owns the click; keep the checkbox visual-only so a click
  // doesn't toggle twice.
  cloudCardCheckbox: { p: 0, pointerEvents: "none" },
  cloudCardName: { flex: 1, fontSize: "13px", fontWeight: 600 },
  cloudCardCount: {
    fontFamily: "monospace",
    fontSize: "11px",
    color: "text.secondary",
  },
  cloudCardControls: {
    mt: 1.25,
    display: "flex",
    flexDirection: "column",
    gap: 1.25,
  },
  cloudCardMultiSelect: {
    width: "100%",
    "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "13px" },
  },
  // SCRIPTS repeater.
  scriptsEmpty: {
    border: "1px dashed",
    borderColor: "grey.400",
    borderRadius: "8px",
    p: 2,
    textAlign: "center",
    fontSize: "12.5px",
    color: "text.secondary",
  },
  scriptRow: (hasError) => ({
    border: "1px solid",
    borderColor: hasError ? "error.main" : "grey.300",
    borderRadius: "8px",
    p: 1.25,
    mb: 1,
  }),
  scriptRowHead: { display: "flex", alignItems: "center", gap: 1, minWidth: 0 },
  scriptRowName: {
    flex: 1,
    minWidth: 0,
    fontFamily: "monospace",
    fontSize: "12px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  scriptRowMeta: {
    fontFamily: "monospace",
    fontSize: "11px",
    color: "text.secondary",
    whiteSpace: "nowrap",
  },
  scriptRowControls: {
    mt: 1.25,
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
    gap: 1.25,
    alignItems: "end",
  },
  scriptCustomToggle: {
    m: 0,
    alignSelf: "center",
    "& .MuiFormControlLabel-label": { fontSize: "12.5px" },
  },
  sectionHeading: {
    fontFamily: "monospace",
    fontSize: "10px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: "text.secondary",
    mb: 1.5,
  },
  sectionHeadRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: 1.5,
  },
  sectionDivider: { my: 2.5 },
  // Old → new comparison row; below sm the pair stacks and the arrow hides.
  pairRow: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "1fr 22px 1fr" },
    alignItems: "end",
    gap: "10px",
    mb: 2,
  },
  pairArrow: {
    display: { xs: "none", sm: "flex" },
    alignItems: "center",
    justifyContent: "center",
    height: "38px",
    color: "text.secondary",
  },
  pairValueBox: {
    height: "38px",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    px: 1.5,
    bgcolor: "grey.100",
    borderRadius: "8px",
    fontFamily: "monospace",
    fontSize: "12.5px",
    color: "text.primary",
  },
  fieldLabel: {
    display: "block",
    fontSize: "11.5px",
    color: "text.secondary",
    mb: "4px",
    "& .MuiFormLabel-asterisk": { color: "error.main" },
  },
  fieldInput: {
    "& .MuiOutlinedInput-root": {
      height: "38px",
      borderRadius: "8px",
      fontSize: "13px",
    },
  },
  // Matches the width of one pair column (1fr of "1fr 22px 1fr" with 10px gaps).
  halfRow: { width: { xs: "100%", sm: "calc(50% - 21px)" } },
  uploadGrid: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
    gap: 1.5,
  },
  uploadCard: (hasError) => ({
    border: "1px dashed",
    borderColor: hasError ? "error.main" : "grey.400",
    bgcolor: "grey.50",
    borderRadius: "9px",
    p: 1.5,
  }),
  uploadHint: { fontSize: "11.5px", color: "text.secondary", mb: 1 },
  uploadFileRow: { display: "flex", alignItems: "center", gap: 1, minWidth: 0 },
  uploadFileName: {
    flex: 1,
    minWidth: 0,
    fontFamily: "monospace",
    fontSize: "12px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  uploadButton: { textTransform: "none", borderRadius: "8px" },
  uploadError: { fontSize: "11.5px", color: "error.main", mt: 0.75 },
  selectAllButton: {
    textTransform: "none",
    fontSize: "11px",
    p: "2px 6px",
    minWidth: 0,
  },
  // Tenant selects (AWS / AZURE / GCP) stacked vertically.
  tenantGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 1.5,
  },
  tenantSelect: {
    height: "38px",
    borderRadius: "8px",
    fontSize: "13px",
  },
  footerSummary: (isError) => ({
    fontSize: "12.5px",
    color: isError ? "error.main" : "text.secondary",
  }),
  actionButtons: { display: "flex", gap: 1 },
  actionButton: { textTransform: "none", borderRadius: "8px" },
  // Discard-changes confirmation shown when closing a dirty drawer.
  discardTitle: { fontSize: "15px", fontWeight: 700 },
  discardText: { fontSize: "13px", color: "text.secondary" },
};

// Portal-style build pages: white header block with title + Reload, then the
// standard portal table filling the remaining height.
export const buildsPageStyles = {
  mainBox: {
    ...tabbedDataGridPage.tabContentMainBox,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  headerGrid: { ...tabbedDataGridPage.gridContainer, flexShrink: 0 },
  dataTable: { flex: 1, minHeight: 0 },
};

export const DbSoftwareBuildsStyles = buildsPageStyles;

export const OsImageBuildsStyles = buildsPageStyles;

// Portal side drawer for a single build: fixed right panel, grey header,
// scrollable body holding the detail cards stacked vertically.
export const buildDrawerStyles = {
  paper: (colors, width = 720) => ({
    width: `min(${width}px, 95vw)`,
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: colors.primary[700],
  }),
  header: (colors) => ({
    px: 3,
    py: 2,
    backgroundColor: colors.grey[900],
    borderBottom: `1px solid ${colors.grey[800]}`,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 2,
    flexShrink: 0,
  }),
  title: (colors) => ({
    fontSize: "15px",
    fontWeight: 700,
    color: colors.grey[100],
  }),
  subtitle: (colors) => ({
    fontSize: "12px",
    color: colors.grey[400],
  }),
  body: { flex: 1, minHeight: 0, overflowY: "auto", p: "16px 20px" },
};

// Portal detail panel: grey canvas holding flat white bordered cards.
export const buildDetailedPanelStyles = {
  mainGrid: (colors) => ({
    py: "12px",
    height: "100%",
    boxSizing: "border-box",
    backgroundColor: colors.surface2,
    borderBottom: `1px solid ${colors.border}`,
  }),
  // Vertical stack variant used inside the right-side drawer.
  drawerGrid: { rowGap: "12px" },
  paper: (colors) => ({
    flex: 1,
    mx: "auto",
    width: "99%",
    p: "14px 16px",
    height: "100%",
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    boxShadow: "none",
  }),
  // Uppercase card label matching the portal drawer sections.
  panelSectionLabel: (colors) => ({
    fontSize: "11px",
    fontWeight: 600,
    color: colors.muted,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    mb: "8px",
  }),
  otherDetailsHorizontalBox: {
    display: "flex",
    direction: "row",
    mt: "5px",
  },
  osImageOtherDetialsItemKeyTypography: {
    minWidth: "70px",
    maxWidth: "70px",
    flexShrink: 0,
  },
  osImageQaButton: {
    m: "0 0 0 2px",
    width: "100px",
  },
  otherDetailsButton: {
    m: "0 0 0 5px",
    width: "20%",
  },
  scriptPathBox: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flexGrow: 1,
    minWidth: 0,
  },
  stepMainBox: (colors) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.surface2,
    border: `1px solid ${colors.border}`,
    p: "4px 4px 4px 12px",
    m: "4px 0",
    borderRadius: "6px",
    minHeight: "36px",
  }),
  stepName: (colors, stepStatus) => {
    const stepColorMap = {
      COMPLETED: colors.okText,
      FAILED: colors.errText,
      RUNNING: colors.link,
    };
    return {
      fontSize: "13px",
      fontWeight: 500,
      color: stepColorMap?.[stepStatus] || colors.body,
    };
  },
  stepErrorIcon: (colors) => ({
    ml: "7px",
    fontSize: 18,
    color: colors.errText,
  }),
  retryIcon: { mr: "5px" },
};

// Build Details card: header, stage table, footer action bar. The Paper is
// edge-to-edge (p: 0) so the table and grey footer run the full card width.
export const buildDetailsCardStyles = {
  paper: (colors) => ({
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    boxShadow: "none",
    overflow: "hidden",
  }),
  header: {
    px: 2.5,
    pt: 2,
    pb: 1.75,
    display: "flex",
    alignItems: "baseline",
    flexWrap: "wrap",
    gap: 1.25,
  },
  headerVersion: {
    fontFamily: "monospace",
    fontSize: "13px",
    color: "text.secondary",
  },
  headerSpacer: { flexGrow: 1 },
  tableWrap: { display: { xs: "none", sm: "block" } },
  headCell: {
    backgroundColor: "grey.50",
    fontFamily: "monospace",
    fontSize: "10px",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "text.secondary",
    fontWeight: 600,
  },
  stageNameCell: { fontWeight: 500 },
  statusCellBox: { display: "flex", alignItems: "center", gap: 1 },
  statusDot: (color) => ({
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    bgcolor: color,
    flexShrink: 0,
  }),
  artifactsCellBox: { display: "flex", gap: 1 },
  // Below sm the table collapses to stacked stage blocks.
  stackedWrap: { display: { xs: "block", sm: "none" }, px: 2.5, pb: 1.5 },
  stackedBlock: (colors) => ({
    border: `1px solid ${colors.border}`,
    borderRadius: "6px",
    p: 1.5,
    mb: 1,
  }),
  stackedBlockHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    mb: 1,
  },
  stackedButtons: { display: "flex", gap: 1, "& > *": { flex: 1 } },
  qaRow: (colors) => ({
    px: 2.5,
    py: 1.5,
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    borderTop: `1px solid ${colors.border}`,
  }),
  footer: (colors) => ({
    bgcolor: colors.surface2,
    px: 2.5,
    py: 1.5,
    borderTop: `1px solid ${colors.border}`,
    display: "flex",
    alignItems: { xs: "stretch", sm: "center" },
    justifyContent: "space-between",
    flexDirection: { xs: "column", sm: "row" },
    gap: 1.5,
  }),
  footerSummary: (isError) => ({
    fontSize: "13px",
    color: isError ? "error.main" : "text.secondary",
  }),
  footerButtonWrap: { display: "flex", width: { xs: "100%", sm: "auto" } },
  footerButton: { width: { xs: "100%", sm: "auto" } },
};
export default styles;
